import { FC, FormEvent, useState, useId } from 'react';
import { DialogProps, NewDataField } from './dialogInterface';
import { useTranslation } from 'react-i18next';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import { DeleteExportNotice } from 'reusable/DeleteExport';

const DeleteDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  note,
  confirmLabel,
  cancelLabel,
  fields,
  handleSubmit,
  deviceId,
  vehicleId,
  exportRecords,
}) => {
  const { t } = useTranslation('common');
  // Hookは早期returnより前で、毎回同じ順序で呼ぶ。isOpenで分岐すると呼び出し順が変わる。
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emptyData: NewDataField = { id: deviceId ?? vehicleId ?? '' };

  /**
   * ADM-24。formの標準送信を止め、削除が終わるまで結果を待つ。
   * 以前は preventDefault を呼んでおらず、削除要求と同時に画面が再読み込みされ、
   * 失敗しても「消えたように見える」ことがあった。
   */
  const handleFormSubmit = async (e: FormEvent<Element>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await handleSubmit(e, emptyData);
    } catch (error) {
      // 失敗の表示は呼び出し側が行う。ここではダイアログを残し、もう一度押せるようにする。
      console.error('Failed to delete', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return inModalLayer(
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {title}
                  </h3>
                  <button type="button"
                    onClick={onClose}
                    className="text-gray-600 hover:text-gray-800 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <div className="flex flex-col m-4 sm:mx-6 sm:mt-6 gap-4">
                  <p>{subtitle}</p>
                  {note ?? <p>{note}</p>}
                  {/* UAT A15。削除の前に、その場で記録を保存できるようにする。 */}
                  {exportRecords ? <DeleteExportNotice onExport={exportRecords} /> : null}
                </div>
                {/* <p className="m-4 sm:mx-6 sm:mt-6">{subtitle}</p> */}
                <form onSubmit={handleFormSubmit}>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-4 m-4 sm:m-6 sm:rounded-lg border sm:border-neutral/50 sm:p-4`}
                  >
                    {fields.map((field, index) => (
                      <div key={index}>
                        <label className="inline-block mb-1 text-overline font-regular text-neutral/100 whitespace-nowrap">
                          {field.label}
                        </label>
                        <p className="text-body-2 font-normal text-neutral/300 break-words">
                          {field.value}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-semibold text-neutral/100 disabled:opacity-60"
                      onClick={onClose}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex justify-center items-center rounded-md bg-error text-subtitle-2 text-white h-12 disabled:opacity-60"
                    >
                      {confirmLabel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteDialog;
